import { Form, Space, Table, TableColumnsType } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { EyeIcon, MinusBordered, PlusBordered } from 'assets/svg';
import ButtonAction from 'components/Button/ButtonAction';
import {
  IPaginatedPurchaseData,
  IPurchaseInvoiceData,
} from 'interfaces/PurchaseInvoice';
import Content from 'layouts/Content';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import formatNumber, { formatCurrency } from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';

interface IProps {
  loading?: boolean;
  data: IPaginatedPurchaseData;
}

const unit = 1;

const TablePurchaseInvoice = ({ data, loading }: IProps) => {
  const [form] = useForm();
  const navigate = useNavigate();

  const columns: TableColumnsType<IPurchaseInvoiceData> = useMemo(() => {
    return [
      {
        width: 50,
        title: 'STT',
        align: 'center',
        dataIndex: 'index',
        // render: (_: any, __: any, index: number) =>
        //   index + 1 + pageSize * (page - 1),
      },
      {
        width: 150,
        title: 'Mã PO',
        dataIndex: 'poId',
        render: (value) => (value ? value : '-'),
      },
      {
        width: 250,
        dataIndex: 'id',
        title: 'Mã dẻ DMS',
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 150,
        title: 'Khách hàng',
        dataIndex: ['tcustomer', 'sapCustomerId'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 250,
        title: 'Tên khách hàng',
        dataIndex: ['tcustomer', 'storeName'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 100,
        title: 'TL Q10',
        dataIndex: 'totalQ10Buy',
        render: (value: any) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }),
      },
      {
        width: 200,
        title: 'TL Q10 sau khi ĐC',
        dataIndex: 'totalQ10Acounting',
        render: (value: any) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }),
      },
      {
        width: 150,
        unit: unit,
        type: 'number',
        align: 'right',
        currency: 'vi-VN',
        title: 'Thành tiền',
        dataIndex: 'finalPrice',
        render: (value) => formatNumber(value) || '-',
      },
      {
        width: 200,
        unit: unit,
        type: 'number',
        align: 'right',
        currency: 'vi-VN',
        title: 'Thành tiền sau khi ĐC',
        dataIndex: 'totalFinalPriceAcounting',
        render: (value) => formatNumber(value) || '-',
      },
      {
        width: 120,
        title: 'Trạng thái',
        dataIndex: 'purchaseStatus',
        render: (value) => (value === 'PUR00_02' ? 'Hoàn thành' : 'Mới tạo'),
      },

      {
        width: 350,
        title: 'Mã dẻ tách',
        dataIndex: 'tpurchaseChilds',
        render: (value) => {
          return value?.length
            ? value?.map((item: any) => item?.id)?.join(', ')
            : '-';
        },
      },
      {
        width: 150,
        title: 'Mã KH cha',
        dataIndex: ['tcustomer', 'mainCustomer', 'id'],
        render: (value) => (value ? value : '-'),
      },
      // {
      //   width: 150,
      //   title: 'Mã Tuyến',
      //   // dataIndex: 'mainCustomerId',
      //   // render: (value) => (value ? value : '-'),
      // },
      {
        width: 250,
        title: 'Đội',
        dataIndex: 'teamSalesId',
      },
      {
        width: 180,
        title: 'Loại thu dẻ',
        dataIndex: ['tpurchType', 'name'],
      },
      {
        width: 350,
        title: 'Mã bảng kê',
        dataIndex: 'tpurchaseOrderQ10',
        render: (value) => {
          return value?.length
            ? value?.map((item: any) => item?.id)?.join(', ')
            : '-';
        },
      },
      {
        width: 150,
        title: 'Mã BTHTT',
        dataIndex: 'ttId',
        render: (value) => (value ? value : '-'),
      },
      {
        width: 200,
        title: 'Thủ kho xác nhận',
        // dataIndex: 'warehouseConfirmation',
      },
      {
        width: 220,
        title: 'Thời gian thủ kho xác nhận',
        // dataIndex: 'warehouseConfirmation',
      },
      {
        width: 200,
        title: 'Kế toán xác nhận',
        // dataIndex: 'warehouseConfirmation',
      },
      {
        width: 220,
        title: 'Thời gian kế toán xác nhận',
        // dataIndex: 'warehouseConfirmation',
      },
      {
        width: 120,
        align: 'center',
        title: 'Chi nhánh',
        dataIndex: 'plantId',
      },
      {
        width: 180,
        title: 'Người tạo',
        dataIndex: ['tcreateUser', 'fullName'],
        className: 'align-center',
      },
      {
        width: 200,
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:MM') : '-',
      },
      {
        width: 180,
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
      },
      {
        width: 200,
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:MM') : '-',
      },
      {
        width: 80,
        fixed: 'right',
        align: 'center',
        render: (_, record) => (
          <Space size="middle" className="text-lg">
            <ButtonAction
              tooltip="Xem chi tiết"
              onClick={
                () =>
                  navigate(`${path.ADD_NEW_PURCHASE_INVOICE}?id=${record?.id}`)
                // navigate(path.PURCHASE_INVOICE_DETAIL, {
                //   state: { id: record.id },
                // })
              }
            >
              <EyeIcon />
            </ButtonAction>
          </Space>
        ),
      },
    ];
  }, []);

  return (
    <Content>
      <Form form={form}>
        <Table<IPurchaseInvoiceData>
          columns={columns}
          loading={loading}
          dataSource={data?.items}
          scroll={{ x: 'max-content' }}
          rowKey={(record: IPurchaseInvoiceData) => record?.id}
          expandable={{
            expandIconColumnIndex: 1,
            expandIcon: ({ expanded, onExpand, record }) => {
              const canExpand = record.children && record.children?.length > 0;
              const svgClasses =
                'inline cursor-pointer hover:bg-blue text-main';
              const buttonClasses = '!inline-flex mr-[6px]';

              return canExpand ? (
                expanded ? (
                  <ButtonAction className={buttonClasses}>
                    <MinusBordered
                      className={svgClasses}
                      onClick={(e) => onExpand(record, e as any)}
                    />
                  </ButtonAction>
                ) : (
                  <ButtonAction className={buttonClasses}>
                    <PlusBordered
                      className={svgClasses}
                      onClick={(e) => onExpand(record, e as any)}
                    />
                  </ButtonAction>
                )
              ) : null;
            },
          }}
          pagination={{
            showSizeChanger: true,
            className: '!mb-0 !mt-5',
            pageSizeOptions: ['10', '20', '50'],
            current: data?.pageInfo?.currentPage || 1,
            pageSize: data?.pageInfo?.itemsPerPage || DEFAULT_PAGE_SIZE,
            total: data?.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
};

export default TablePurchaseInvoice;
